import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { MultiValue } from "react-select";

const LocationCreation = () => {
  const navigate = useNavigate();
  const [selectedMakes, setSelectedMakes] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [locationName, setLocationName] = useState<string>("");
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");

  interface MakeModelListResponse {
    [key: string]: string[];
  }

  const { data: makeModelData } = useQuery<MakeModelListResponse>({
    queryKey: ["makeModelList"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.MAKE_MODEL_LIST);
      return response.data;
    },
  });

  const makeOptions = makeModelData
    ? [
        { value: "Select All Make", label: "Select All Make" },
        ...Object.keys(makeModelData).map((make) => ({
          value: make,
          label: make,
        })),
      ]
    : [{ value: "Select All Make", label: "Select All Make" }];

  const queryClient = useQueryClient();

  const { mutate: createLocation, isPending } = useMutation({
    mutationFn: async (payload: any) => {
      const response = await axiosInstance.post(
        API_URL.CREATE_VEHICLE_LOCATIONS,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Location created successfully");
      navigate(-1);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create location"
      );
    },
  });

  const handleSaveLocation = () => {
    if (!locationName || !latitude || !longitude) {
      toast.error("Please fill in location details (Name, Latitude, Longitude)");
      return;
    }

    if (selectedMakes.length === 0) {
      toast.error("Please select at least one Make");
      return;
    }

    const isSelectAllManually = selectedMakes.some((option) => option.value === "Select All Make");
    
    // If "Select All" is active, we pass ["Select All Make"] and the toggle
    // Otherwise we pass the list of selected values
    const vehicleTypes = isSelectAllManually 
      ? ["Select All Make"] 
      : selectedMakes.map((option) => option.value);

    const payload: any = {
      location: locationName,
      latitude: latitude,
      longitude: longitude,
      vehicle_types: vehicleTypes,
    };

    if (isSelectAllManually) {
      payload.create_for_all_vehicle_types = true;
    }

    createLocation(payload);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleMakesChange = (
    newValue: MultiValue<{ value: string; label: string }>,
    actionMeta: any
  ) => {
    if (actionMeta.action === "select-option" && actionMeta.option.value === "Select All Make") {
      setSelectedMakes(makeOptions);
    } else if (actionMeta.action === "deselect-option" && actionMeta.option.value === "Select All Make") {
      setSelectedMakes([]);
    } else if (actionMeta.action === "deselect-option") {
      // If we deselect any regular option, we must also deselect "Select All Make" if it was checked
      setSelectedMakes(newValue.filter(o => o.value !== "Select All Make"));
    } else if (newValue.length === makeOptions.length - 1 && !newValue.some(o => o.value === "Select All Make")) {
        // If all regular options are selected manually, add "Select All Make"
        setSelectedMakes(makeOptions);
    } else {
      setSelectedMakes(newValue);
    }
  };

  return (
    <div>
      <div className="col-12">
        <div className="row">
          <div className="col-md-12 text-start div-bg-invoice-details pt-3">
            <div className="mb-3">
              <button className="btn all_button_style" onClick={handleBack}>
                Back
              </button>
            </div>
            <div className="card card-bg-invoice-details">
              <div className="card-body px-5">
                <h4 className="mb-4">Create Location for Service Centre</h4>

                <div className="row pt-3">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="makeSelect" className="form-label">
                      Make
                    </label>
                    <Select
                      isMulti
                      options={makeOptions}
                      value={selectedMakes}
                      onChange={handleMakesChange}
                      placeholder="Select Make(s)"
                      classNamePrefix="react-select"
                      hideSelectedOptions={false}
                      closeMenuOnSelect={false}
                      components={{
                        Option: (props) => {
                          const { isDisabled, isSelected, label, innerProps } = props;
                          return (
                            <div
                              {...innerProps}
                              style={{
                                padding: "8px 12px",
                                cursor: isDisabled ? "not-allowed" : "pointer",
                                backgroundColor: isSelected ? "#f0f0f0" : "transparent",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                style={{ pointerEvents: "none" }}
                              />
                              <span>{label}</span>
                            </div>
                          );
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Location Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      placeholder="Enter Location Name"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Latitude</label>
                    <input
                      type="number"
                      className="form-control"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      placeholder="Enter Latitude"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Longitude</label>
                    <input
                      type="number"
                      className="form-control"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      placeholder="Enter Longitude"
                    />
                  </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12 text-end">
                        <button 
                            className="btn btn-success"
                            onClick={handleSaveLocation}
                            disabled={isPending}
                        >
                            {isPending ? "Saving..." : "Save Location"}
                        </button>
                    </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationCreation;
